import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Play, Users, Briefcase, MapPin, Video, Camera, Film, Mic, Palette, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import SearchBar from "../components/SearchBar";
import ProfileCard from "../components/ProfileCard";
import BannerDisplay from "../components/BannerDisplay";
import SEO from "../components/SEO";
import axios from "axios";
import { API } from "../App";

const HERO_IMAGE = "https://images.pexels.com/photos/2335052/pexels-photo-2335052.jpeg?auto=compress&cs=tinysrgb&w=1920";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_videocrew-ct/artifacts/ipgpa8tb_VGCT_LOGO.png";

const categoryIcons = {
  "Videographer": Video,
  "Cinematographer": Film,
  "Editor": Palette,
  "Camera Operator": Camera,
  "Sound Recordist": Mic,
  "Drone Operator": Camera,
  "VFX Artist": Sparkles,
  "Animator": Play,
};

export default function Home() {
  const navigate = useNavigate();
  const [featuredProfiles, setFeaturedProfiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ profiles: 0, jobs: 0, locations: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profilesRes, categoriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/profiles/featured?limit=6`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/stats`),
      ]);
      
      setFeaturedProfiles(profilesRes.data);
      setCategories(categoriesRes.data.slice(0, 8));
      setStats({
        profiles: statsRes.data.creatives,
        jobs: statsRes.data.active_jobs,
        locations: statsRes.data.locations,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <SEO 
        url="/"
        title="Cape Town's Premier Creative Directory"
        description="Find the best videographers, cinematographers, editors, and creative professionals in Cape Town. The premier directory for film and video production talent in South Africa."
        keywords={["videography", "cape town", "film production", "creative directory"]}
      />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden spotlight">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Cape Town Production"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/70 via-zinc-950/80 to-zinc-950" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 text-center">
          <div className="animate-fadeInUp stagger-1">
            <img
              src={LOGO_URL}
              alt="Videography Cape Town"
              className="h-24 md:h-32 mx-auto mb-8"
            />
          </div>
          
          <h1 className="font-heading text-display uppercase leading-none mb-6 animate-fadeInUp stagger-2">
            CAPE TOWN'S
            <br />
            <span className="text-orange-500">CREATIVE</span> DIRECTORY
          </h1>
          
          <p className="text-subtitle text-zinc-400 max-w-2xl mx-auto mb-10 animate-fadeInUp stagger-3">
            Find the perfect videographers, cinematographers, editors, and creative 
            professionals for your next production in the Mother City.
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-12 animate-fadeInUp stagger-4">
            <SearchBar variant="hero" />
          </div>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 animate-fadeInUp stagger-5">
            <div className="text-center">
              <div className="font-heading text-4xl md:text-5xl text-white" data-testid="stat-creatives">{stats.profiles}</div>
              <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest mt-1">Creatives</div>
            </div>
            <div className="text-center">
              <div className="font-heading text-4xl md:text-5xl text-white" data-testid="stat-active-jobs">{stats.jobs}</div>
              <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest mt-1">Active Jobs</div>
            </div>
            <div className="text-center">
              <div className="font-heading text-4xl md:text-5xl text-white" data-testid="stat-locations">{stats.locations}</div>
              <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest mt-1">Locations</div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-zinc-600 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-orange-500 rounded-full mt-2 animate-pulse" />
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto" data-testid="categories-section">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h2 className="font-heading text-title uppercase">
              BROWSE BY <span className="text-orange-500">CATEGORY</span>
            </h2>
            <p className="text-zinc-500 mt-2">Find specialists in every creative field</p>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate("/categories")}
            className="hidden md:flex items-center gap-2 text-zinc-400 hover:text-white"
            data-testid="view-all-categories-btn"
          >
            VIEW ALL <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((category, index) => {
            const IconComponent = categoryIcons[category.name] || Video;
            return (
              <button
                key={category.category_id}
                onClick={() => navigate(`/browse?skills=${encodeURIComponent(category.name)}`)}
                className="category-card group p-6 bg-zinc-900 border border-zinc-800 hover:border-orange-500/50 transition-all duration-300 text-left"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`category-${category.slug}`}
              >
                <IconComponent className="h-8 w-8 text-orange-500 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-heading text-xl text-white mb-1">{category.name}</h3>
                <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider">
                  {category.profile_count || 0} profiles
                </p>
              </button>
            );
          })}
        </div>

        <Button
          variant="outline"
          onClick={() => navigate("/categories")}
          className="w-full mt-6 md:hidden border-zinc-700 text-zinc-300"
        >
          VIEW ALL CATEGORIES
        </Button>
      </section>

      {/* Banner Ad - Hero placement */}
      <section className="px-4 md:px-8 py-6 bg-zinc-950">
        <div className="max-w-7xl mx-auto">
          <BannerDisplay placement="hero" />
        </div>
      </section>

      {/* Featured Profiles Section */}
      <section className="py-20 px-4 md:px-8 bg-zinc-950" data-testid="featured-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="font-heading text-title uppercase">
                FEATURED <span className="text-orange-500">CREATIVES</span>
              </h2>
              <p className="text-zinc-500 mt-2">Top-rated professionals in Cape Town</p>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate("/browse")}
              className="hidden md:flex items-center gap-2 text-zinc-400 hover:text-white"
              data-testid="view-all-creatives-btn"
            >
              VIEW ALL <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-cinematic bg-zinc-900 skeleton" />
              ))}
            </div>
          ) : featuredProfiles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProfiles.map((profile) => (
                <ProfileCard key={profile.profile_id} profile={profile} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Users className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500">No featured profiles yet</p>
              <Button
                onClick={() => navigate("/signup")}
                className="mt-4 bg-orange-600 hover:bg-orange-500"
              >
                BE THE FIRST
              </Button>
            </div>
          )}

          <Button
            variant="outline"
            onClick={() => navigate("/browse")}
            className="w-full mt-8 md:hidden border-zinc-700 text-zinc-300"
          >
            VIEW ALL CREATIVES
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 md:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-600/20 to-red-600/20 opacity-50" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="font-heading text-title uppercase mb-6">
            READY TO <span className="text-orange-500">GET DISCOVERED?</span>
          </h2>
          <p className="text-zinc-400 text-lg mb-8 max-w-2xl mx-auto">
            Join Cape Town's premier creative directory. Showcase your work, 
            connect with clients, and grow your career in film and video production.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate("/signup")}
              className="w-full sm:w-auto bg-orange-600 hover:bg-orange-500 text-white px-8 py-6 text-lg"
              data-testid="cta-create-profile-btn"
            >
              CREATE YOUR PROFILE
            </Button>
            <Button
              onClick={() => navigate("/post-job")}
              variant="outline"
              className="w-full sm:w-auto border-zinc-600 text-zinc-300 hover:bg-zinc-800 px-8 py-6 text-lg"
              data-testid="cta-post-job-btn"
            >
              POST A JOB
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 md:px-8 bg-zinc-950" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-title uppercase">
              HOW IT <span className="text-orange-500">WORKS</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "CREATE YOUR PROFILE",
                description: "Sign up and build your professional profile with your portfolio, skills, and rates.",
                icon: Users,
              },
              {
                step: "02",
                title: "GET DISCOVERED",
                description: "Clients search and filter to find the perfect creative for their production needs.",
                icon: Camera,
              },
              {
                step: "03",
                title: "LAND THE GIG",
                description: "Connect directly with clients, negotiate terms, and start working on exciting projects.",
                icon: Briefcase,
              },
            ].map((item, index) => (
              <div
                key={index}
                className="relative p-8 bg-zinc-900 border border-zinc-800"
              >
                <div className="font-mono text-6xl font-bold text-zinc-800 absolute top-4 right-4">
                  {item.step}
                </div>
                <item.icon className="h-10 w-10 text-orange-500 mb-6" />
                <h3 className="font-heading text-xl text-white mb-3">{item.title}</h3>
                <p className="text-zinc-500">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations Preview */}
      <section className="py-20 px-4 md:px-8" data-testid="locations-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="font-heading text-title uppercase">
                CAPE TOWN <span className="text-orange-500">LOCATIONS</span>
              </h2>
              <p className="text-zinc-500 mt-2">Find creatives in your area</p>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate("/locations")}
              className="hidden md:flex items-center gap-2 text-zinc-400 hover:text-white"
              data-testid="view-all-locations-btn"
            >
              VIEW ALL <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex flex-wrap gap-3">
            {[
              "Cape Town CBD",
              "Sea Point",
              "Camps Bay",
              "Woodstock",
              "Constantia",
              "Hout Bay",
              "Stellenbosch",
              "Table View",
            ].map((location) => (
              <button
                key={location}
                onClick={() => navigate(`/browse?location=${encodeURIComponent(location)}`)}
                className="flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 hover:border-orange-500/50 text-zinc-300 hover:text-white transition-all"
              >
                <MapPin className="h-4 w-4 text-orange-500" />
                {location}
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
